import type JCE from '@chesscom/js-chess-engine';
import { Game } from 'chessboard/model/game/types';
import type { MoveListComponent } from '../config/types';
import { isMatchingNode } from './is-matching-node';

/**
 * gets the whole move number from a moveListNode
 */
export function getWholeMoveNumber(
  node: Game.Move.Node | undefined,
  startingMoveNumber: number = 1,
) {
  const moveNumber = node?.moveNumber ?? 0;
  const color = node?.color;
  const firstMoveByBlack =
    (moveNumber % 2 === 0 && color === Game.ColorsAsNumbers.Black) ||
    (moveNumber % 2 !== 0 && color === Game.ColorsAsNumbers.White);
  const wholeMoveNumber =
    startingMoveNumber +
    Math.floor(moveNumber / 2) +
    (firstMoveByBlack && color === Game.ColorsAsNumbers.White ? 1 : 0);
  return wholeMoveNumber;
}

/**
 * Checks if the node is contained in the moveListNodes.
 * Recursively checks inline variations.
 */
export function moveListNodesContainsNode(
  node: JCE.Node.Model,
  moveListNodes: MoveListComponent.MoveListNode[],
): boolean {
  return moveListNodes.some(
    (moveListNode) =>
      isMatchingNode(node, moveListNode.node) ||
      moveListNode.inlineVariations?.some((inlineVariation) =>
        moveListNodesContainsNode(node, inlineVariation),
      ),
  );
}

/**
 * Builds a new array of moveListNodes with any instances of the supplied node replaced
 * Recursively updates inline variations.
 */
export function rebuildMoveListNodesWithNode(
  node: JCE.Node.Model,
  moveListNodes: MoveListComponent.MoveListNode[],
): MoveListComponent.MoveListNode[] {
  return moveListNodes.map((moveListNode) => {
    const newNode = {
      ...moveListNode,
      node: isMatchingNode(node, moveListNode.node) ? node : moveListNode.node,
    };

    if (moveListNode.inlineVariations) {
      newNode.inlineVariations = moveListNode.inlineVariations.map((inlineVariation) =>
        rebuildMoveListNodesWithNode(node, inlineVariation),
      );
    }
    return newNode;
  });
}
